import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useAuthStore } from '@/stores/authStore'
import subscriptionService, { Invoice } from '@/services/subscription'
import './InvoicesPage.css'

const InvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { isAuthenticated } = useAuthStore()

  useEffect(() => {
    if (isAuthenticated) {
      fetchInvoices()
    } else {
      setLoading(false)
    }
  }, [isAuthenticated])

  const fetchInvoices = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await subscriptionService.getMyInvoices()
      // Trier les factures par date de création (plus récentes en premier)
      const sortedInvoices = data.sort((a, b) => {
        const dateA = a.created ? new Date(a.created).getTime() : 0
        const dateB = b.created ? new Date(b.created).getTime() : 0
        return dateB - dateA
      })
      setInvoices(sortedInvoices)
    } catch (err: any) {
      console.error('Erreur lors de la récupération des factures:', err)
      setError(err.response?.data?.message || 'Impossible de charger les factures')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Date non disponible'
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100) // Stripe utilise les centimes
  }

  const getStatusLabel = (status: string | null): string => {
    if (!status) return 'Inconnu'
    const statusMap: { [key: string]: string } = {
      paid: 'Payée',
      open: 'Ouverte',
      void: 'Annulée',
      uncollectible: 'Irrecoverable',
      draft: 'Brouillon'
    }
    return statusMap[status.toLowerCase()] || status
  }

  const getStatusClass = (status: string | null): string => {
    if (!status) return 'status-badge-unknown'
    const statusLower = status.toLowerCase()
    if (statusLower === 'paid') return 'status-badge-paid'
    if (statusLower === 'open') return 'status-badge-open'
    if (statusLower === 'void' || statusLower === 'uncollectible') return 'status-badge-void'
    return 'status-badge-unknown'
  }

  if (!isAuthenticated) {
    return (
      <div className="invoices-container">
        <div className="error-message">
          Vous devez être connecté pour voir vos factures.
        </div>
      </div>
    )
  }

  return (
    <div className="invoices-container">
      <Helmet>
        <title>Mes factures - EdenPlaisir.com</title>
        <meta name="description" content="Consultez l'historique de toutes vos factures sur EdenPlaisir.com" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      
      <div className="invoices-hero">
        <h1>Mes factures</h1>
        <p>Consultez l'historique de toutes vos factures</p>
      </div>

      {loading && (
        <div className="loading">Chargement des factures...</div>
      )}

      {error && (
        <div className="error-message">{error}</div>
      )}

      {!loading && !error && invoices.length === 0 && (
        <div className="no-invoices">
          <p>Aucune facture disponible pour le moment.</p>
        </div>
      )}

      {!loading && !error && invoices.length > 0 && (
        <div className="invoices-list">
          {invoices.map((invoice) => (
            <div key={invoice.id} className="invoice-card">
              <div className="invoice-header">
                <div className="invoice-info">
                  <h3>
                    Facture {invoice.number || invoice.id.slice(-8)}
                    <span className={`status-badge ${getStatusClass(invoice.status)}`}>
                      {getStatusLabel(invoice.status)}
                    </span>
                  </h3>
                  {invoice.description && (
                    <p className="invoice-description">{invoice.description}</p>
                  )}
                </div>
                <div className="invoice-amount">
                  <div className="amount-value">
                    {formatCurrency(invoice.amount_due, invoice.currency)}
                  </div>
                  {invoice.amount_paid > 0 && invoice.amount_paid !== invoice.amount_due && (
                    <div className="amount-paid">
                      Payé: {formatCurrency(invoice.amount_paid, invoice.currency)}
                    </div>
                  )}
                </div>
              </div>

              <div className="invoice-details">
                <div className="detail-row">
                  <span className="detail-label">Date de création:</span>
                  <span className="detail-value">{formatDate(invoice.created)}</span>
                </div>
                {invoice.period_start && (
                  <div className="detail-row">
                    <span className="detail-label">Période:</span>
                    <span className="detail-value">
                      {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                    </span>
                  </div>
                )}
                {invoice.subscription_id && (
                  <div className="detail-row">
                    <span className="detail-label">Abonnement:</span>
                    <span className="detail-value">{invoice.subscription_id.slice(-12)}</span>
                  </div>
                )}
              </div>

              <div className="invoice-actions">
                {invoice.hosted_invoice_url && (
                  <a
                    href={invoice.hosted_invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="invoice-button invoice-button-view"
                  >
                    Voir la facture
                  </a>
                )}
                {invoice.invoice_pdf && (
                  <a
                    href={invoice.invoice_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="invoice-button invoice-button-download"
                    download
                  >
                    Télécharger le PDF
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default InvoicesPage

